//src/pages/ResetPasswordPage.tsx
import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Moon,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";

const NORMEX_LOGO_SRC = "/logo-v3-preto.png";

export default function ResetPasswordPage() {
  const { resetPassword, isAuthenticated, isLoading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token")?.trim() || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (authLoading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!token) {
      toast.error("Link de redefinição inválido.");
      return;
    }

    if (!password || !confirmPassword) {
      toast.error("Preencha a nova senha e a confirmação.");
      return;
    }

    if (password.length < 8) {
      toast.error("A nova senha deve ter pelo menos 8 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("A confirmação da senha não confere.");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, password);
      setIsSuccess(true);
      toast.success("Senha redefinida com sucesso.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível redefinir a senha.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleGoToLogin() {
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_hsl(var(--primary)/0.14),_transparent_34%),linear-gradient(135deg,_hsl(var(--background)),_hsl(var(--muted)/0.35))] p-4 text-foreground">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute right-4 top-4 z-10 rounded-full border border-border/60 bg-background/80 shadow-sm backdrop-blur hover:bg-background"
        aria-label="Alternar tema"
      >
        {theme === "dark" ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
      </Button>

      <main className="flex min-h-screen items-center justify-center py-8">
        <Card className="w-full max-w-lg overflow-hidden rounded-[2rem] border-border/60 bg-card/95 shadow-2xl shadow-primary/10 backdrop-blur">
          <CardHeader className="space-y-5 px-6 pb-2 pt-8 text-center sm:px-10">
            <div className="flex justify-center">
              <div className="rounded-2xl bg-white px-5 py-3 shadow-sm ring-1 ring-border/50">
                <img
                  src={NORMEX_LOGO_SRC}
                  alt="Normex"
                  className="h-11 w-auto object-contain"
                />
              </div>
            </div>

            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-primary/10 text-primary shadow-lg shadow-primary/10 ring-1 ring-primary/15">
                <ShieldCheck className="h-8 w-8" />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-foreground">
                Redefinir senha
              </h1>
              <p className="mx-auto max-w-sm text-base leading-relaxed text-muted-foreground">
                Você está redefinindo a senha do app da <span className="font-semibold text-primary">Normex</span>.
                Crie uma nova senha segura para acessar sua conta.
              </p>
            </div>
          </CardHeader>

          {isSuccess ? (
            <>
              <CardContent className="space-y-4 px-6 pt-6 sm:px-10">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>

                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-950 dark:text-foreground">Senha atualizada</p>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Sua senha foi redefinida com sucesso. Agora você já pode acessar o app da Normex com a nova credencial.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex-col gap-3 px-6 pb-8 pt-2 sm:px-10">
                <Button className="h-12 w-full rounded-xl text-base font-semibold" onClick={handleGoToLogin}>
                  Ir para o login
                </Button>
              </CardFooter>
            </>
          ) : !token ? (
            <>
              <CardContent className="space-y-4 px-6 pt-6 sm:px-10">
                <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
                  <p className="text-sm font-semibold text-slate-950 dark:text-foreground">Link inválido</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Este link de redefinição da Normex está incompleto ou inválido. Solicite um novo e-mail de recuperação.
                  </p>
                </div>
              </CardContent>

              <CardFooter className="flex-col gap-3 px-6 pb-8 pt-2 sm:px-10">
                <Button asChild className="h-12 w-full rounded-xl text-base font-semibold">
                  <Link to="/forgot-password">Solicitar novo link</Link>
                </Button>

                <Button asChild variant="ghost" className="h-11 w-full rounded-xl font-semibold">
                  <Link to="/login">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar para o login
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-5 px-6 pt-6 sm:px-10">
                <div className="flex items-start gap-4 rounded-2xl border border-primary/15 bg-primary/5 p-4 text-sm text-muted-foreground shadow-sm">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-background text-primary ring-1 ring-primary/15">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <div className="space-y-1.5 text-left">
                    <p className="font-semibold text-slate-950 dark:text-foreground">Sua senha deve ter:</p>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <span>Pelo menos 8 caracteres.</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-left">
                  <Label htmlFor="password" className="text-sm font-semibold text-slate-950 dark:text-foreground">
                    Nova senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Digite a nova senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      className="h-12 rounded-xl pr-11 text-base"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-left">
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold text-slate-950 dark:text-foreground">
                    Confirmar nova senha
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Repita a nova senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className="h-12 rounded-xl pr-11 text-base"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? "Ocultar confirmação de senha" : "Mostrar confirmação de senha"}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-2xl border border-primary/15 bg-card p-4 text-sm leading-relaxed text-muted-foreground shadow-sm">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <span>
                    Ao confirmar, o link será consumido e as sessões antigas do usuário serão invalidadas pelo backend.
                  </span>
                </div>
              </CardContent>

              <CardFooter className="flex-col gap-3 px-6 pb-8 pt-2 sm:px-10">
                <Button type="submit" className="h-12 w-full rounded-xl text-base font-semibold" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Salvar nova senha
                </Button>

                <Button asChild variant="ghost" className="h-11 w-full rounded-xl font-semibold text-primary hover:text-primary">
                  <Link to="/login">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar para o login
                  </Link>
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </main>
    </div>
  );
}
